import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { KnowledgeMap } from '@reados/contracts';
import { createKnowledgeMapFrame } from './knowledge-map-frame';
import { parseKnowledgeMapEvent, projectKnowledgeMap, validKnowledgeMapViewport, type KnowledgeMapMenu, type KnowledgeMapViewport } from './knowledge-map';
import './knowledge-map.css';

export interface ReadOSKnowledgeMapProps {
  map: KnowledgeMap;
  documentPageCount?: number;
  pageNumbersByNodeId?: Record<string, number>;
  viewport?: KnowledgeMapViewport | null;
  onViewportChange?: (viewport: KnowledgeMapViewport) => void;
  onNavigateNode?: (nodeID: string) => void;
  onShowAnswers?: (nodeID: string) => void;
  onShowMenu?: (menu: KnowledgeMapMenu) => void;
  fitRequest?: number;
  colorScheme?: 'light' | 'dark';
  selectedNodeId?: string;
  assetBaseUrl?: string;
}

export function ReadOSKnowledgeMap(props: ReadOSKnowledgeMapProps) {
  const { map, documentPageCount = 0, pageNumbersByNodeId, fitRequest = 0, colorScheme = 'light', selectedNodeId, assetBaseUrl = '/vendor/reados/' } = props;
  const iframe = useRef<HTMLIFrameElement>(null);
  const token = useRef(crypto.randomUUID());
  const latest = useRef(props); latest.current = props;
  const [html, setHTML] = useState('');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const projection = useMemo(() => {
    try { return { payload: projectKnowledgeMap(map, pageNumbersByNodeId, documentPageCount, selectedNodeId), error: '' }; }
    catch (reason) { return { payload: null, error: reason instanceof Error ? reason.message : '知识地图结构无效' }; }
  }, [map, pageNumbersByNodeId, documentPageCount, selectedNodeId]);
  // Viewport saves should not continuously rerender the graph while scrolling.
  // Native renderer keeps its current viewport when a same-map payload changes.
  const renderedMap = useRef('');

  useEffect(() => {
    if (!projection.error) return;
    // A corrected map mounts a fresh iframe and must wait for its own ready
    // event; a ready flag left over from the rejected map would lose render.
    setReady(false); renderedMap.current = '';
  }, [projection.error]);

  useEffect(() => {
    let cancelled = false;
    setReady(false); setError(''); setHTML(''); renderedMap.current = '';
    void createKnowledgeMapFrame(assetBaseUrl, token.current, location.origin).then(value => { if (!cancelled) setHTML(value); })
      .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : '知识地图加载失败'); });
    return () => { cancelled = true; };
  }, [assetBaseUrl]);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.source !== iframe.current?.contentWindow || event.origin !== 'null') return;
      const data = parseKnowledgeMapEvent(event.data, token.current);
      if (!data) return;
      if (data.type === 'ready') { setReady(true); return; }
      if (data.type === 'error') { setError(data.message); return; }
      if (data.type === 'rendered') { setError(''); return; }
      const context = latest.current;
      if (data.mapID !== context.map.id) return;
      if (data.action === 'viewport') { context.onViewportChange?.({ x: data.x, y: data.y, scale: data.scale }); return; }
      const node = context.map.nodes.find(node => node.id === data.nodeID);
      if (!node) return;
      if (data.action === 'jump') context.onNavigateNode?.(node.id);
      else if (data.action === 'showAnswers' && node.answers?.length) context.onShowAnswers?.(node.id);
      else if (data.action === 'showMenu') context.onShowMenu?.({ nodeID: node.id, normalizedX: data.normalizedX, normalizedY: data.normalizedY });
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  useEffect(() => {
    if (!ready || !projection.payload) return;
    const viewport = renderedMap.current !== map.id && validKnowledgeMapViewport(latest.current.viewport) ? latest.current.viewport : null;
    renderedMap.current = map.id;
    iframe.current?.contentWindow?.postMessage({ channel: 'reados-knowledge-map-v1', token: token.current, type: 'render', payload: projection.payload, viewport, colorScheme }, '*');
  }, [ready, projection.payload, colorScheme, map.id]);

  useEffect(() => {
    if (!ready || !fitRequest) return;
    iframe.current?.contentWindow?.postMessage({ channel: 'reados-knowledge-map-v1', token: token.current, type: 'fit' }, '*');
  }, [ready, fitRequest]);

  const message = projection.error || error;
  return <div className="reados-knowledge-map" data-renderer="reados-ipad-knowledge-map">
    {!ready && !message && <div className="reados-knowledge-map-status" role="status">正在准备知识地图…</div>}
    {message && <div className="reados-knowledge-map-error" role="alert">{message}</div>}
    {html && !projection.error && <iframe ref={iframe} className="reados-knowledge-map-frame" title="ReadOS 知识地图" srcDoc={html} sandbox="allow-scripts" referrerPolicy="no-referrer" />}
  </div>;
}
